/* eslint-disable max-len */
import React, {
	useMemo,
	useCallback,
	useState
} from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { GatsbyImage } from 'gatsby-plugin-image';
import { getShopifyImage } from 'gatsby-source-shopify';

import { StoreContext } from '../../context/store-context';
import { getProductPageUrl, isColorSwatchesProduct } from '../../utils';
import { formatPrice } from '../../utils/format-price';
import { gtmRemoveFromCart } from '../../utils/enhanced-ecommerce';

import CustomAttributes from './CustomAttributes';
import NumericInput from '../NumericInput';
import Link from '../Link';

const ns = `line-item`;

const LineItem = ({ checkout, item }) => {
	const {
		removeLineItems,
		updateLineItem,
		loading,
		cartFlyoutClose,
		checkout: physicalCheckout,
	} = React.useContext(StoreContext);

	const [quantity, setQuantity] = useState(item.quantity);

	React.useEffect(() => {
		setQuantity(item.quantity);
	}, [item.quantity]);

	/**
	 * Item image
	 */

	const variantImage = {
		...item?.variant?.image,
		originalSrc: item?.variant?.image?.url,
	};

	const image = useMemo(() => {
		if (variantImage.url) {
			return getShopifyImage({
				image: variantImage,
				layout: 'constrained',
				crop: 'contain',
				width: 180,
				height: 128,
			});
		}

		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variantImage?.url]);

	/**
	 * Item price
	 */
	const additionalPrice = +item?.attributes?.find(attr => attr.key === '_additional_price')?.value || 0;
	const price = formatPrice(
		item?.cost?.totalAmount?.currencyCode,
		Number(item?.merchandise?.price?.amount) + additionalPrice
	);

	/**
	 * Item remove
	 */

	const RemoveButton = Link;

	const handleRemove = e => {
		e.preventDefault();
		e.stopPropagation();

		gtmRemoveFromCart(item);

		removeLineItems(checkout.id, [item.id]);

		// remove physical addons
		const physicalAddons = physicalCheckout.lineItems
			.filter(lineItem => lineItem?.attributes?.some(attr => attr.key === '_classVariantId' && attr.value === item.variant.id));
		if (physicalAddons.length) {
			removeLineItems(physicalCheckout.id, physicalAddons.map(addonLineItem => addonLineItem.id));
		}
	};

	const uli = debounce(
		_quantity => {
			const newLineitem = [{
				id: item.id,
				merchandiseId: item.merchandise.id,
				quantity: _quantity,
			}];
			updateLineItem(checkout.id, newLineitem);
		},
		300
	);

	/**
	 * Item quantity
	 */

	const debouncedUli = useCallback(value => {
		uli(value);
	// eslint-disable-next-line
	}, []);

	const handleQuantityChange = value => {
		if (value !== '' && Number(value) < 1) {
			return;
		}

		setQuantity(value);

		if (Number(value) >= 1) {
			debouncedUli(value);
		}
	};

	function doIncrement(e) {
		e.preventDefault();
		e.stopPropagation();
		handleQuantityChange(Number(quantity || 0) + 1);
	}

	function doDecrement(e) {
		e.preventDefault();
		e.stopPropagation();
		handleQuantityChange(Number(quantity || 0) - 1);
	}

	const itemUsesQuantity = () => {
		const typesWithForcedQuantity = new Set([
			'class',
			'class_hidden',
			'course_manual',
			'event',
			'streaming_video',
			'access',
		]);

		return !typesWithForcedQuantity.has(item?.variant?.product?.productType?.toLowerCase()) && !isColorSwatchesProduct(item?.variant?.product);
	};

	/**
	 * Item selected
	 */

	const renderSelectedOptions = lineItem => {
		if (!lineItem?.variant?.title || lineItem.variant.title === 'Default Title') return null;

		const mainOptions = lineItem.variant.title.split('/').join(' - ');
		const additionalOptions = item?.attributes?.find(attr => attr.key.startsWith('_additional_options'))?.value; // 'option 1 - option 2 - option 3'
		const selectedOptions = [mainOptions, additionalOptions].filter(Boolean).join(' - ');

		return (
			<div className={`${ ns }__options`}>
				{selectedOptions}
			</div>
		);
	};

	/**
	 * Item page url
	 */

	const productLink = getProductPageUrl(item.variant.product);

	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }--no-link`]: !productLink,
	});

	return (
		<li className={rootClassnames}>
			<Link
				href={productLink}
				onClick={cartFlyoutClose}
			>
				<div className={`${ ns }__top`}>
					{image && (
						<div className={`${ ns }__left`}>
							<div className={`${ ns }__image`}>
								<GatsbyImage
									key={variantImage.url}
									image={image}
									width={90}
									height={64}
									alt={variantImage.altText ?? item.variant.title}
								/>
							</div>
						</div>
					)}
					<div className={`${ ns }__right`}>
						<div className={`${ ns }__right-container`}>
							<p className={`${ ns }__title`}>{item.merchandise.product.title}</p>
							<div className={`${ ns }__options-attributes`}>
								{renderSelectedOptions(item)}
								<CustomAttributes attributes={item?.attributes} />
							</div>
							<div className={`${ ns }__price`}>
								{price}
							</div>
						</div>
					</div>
				</div>
				<div className={`${ ns }__bottom`}>
					<div className={`${ ns }__quantity`}>
						{itemUsesQuantity() && (
							<NumericInput
								disabled={loading}
								value={quantity}
								aria-label={`Quantity`}
								onIncrement={doIncrement}
								onDecrement={doDecrement}
							/>
						)}
					</div>
					<div className={`${ ns }__remove`}>
						<RemoveButton
							variant={`cta`}
							onClick={handleRemove}
						>
							Remove
						</RemoveButton>
					</div>
				</div>
			</Link>
		</li>
	);
};

export default LineItem;
